import { useEffect, useMemo, useState } from 'react';
import { DialogMod, DropSelect, toast } from '@zawarski/eti-ui';
import { DialogMultiSelect } from '@components/rep';
import { SC_COUNTIES } from '@constants/index';
import { useChangeLcertYearMutation, useLcertYearsQuery } from '@services/lcertApi';
import { errorMessage } from '@utils/index';
import { availableYears } from './lcertSections';

interface NewYearDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Create new year": seeds one Incomplete letter per county for the chosen
 * year; the selected counties are flagged for the off-year non-evaluated
 * medical service drill section.
 */
const NewYearDialog = ({ open, onOpenChange }: NewYearDialogProps) => {
  const { data: usedYears = [] } = useLcertYearsQuery(undefined, { skip: !open });
  const [changeYear, { isLoading }] = useChangeLcertYearMutation();
  const [year, setYear] = useState<string | undefined>(undefined);
  const [counties, setCounties] = useState<(string | number)[]>([]);
  const [attempted, setAttempted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setYear(undefined);
      setCounties([]);
      setAttempted(false);
      setServerError(null);
    }
  }, [open]);

  const yearOptions = useMemo(
    () => availableYears(usedYears).map((y) => ({ value: y, label: y })),
    [usedYears],
  );
  const countyOptions = useMemo(
    () => [...SC_COUNTIES].sort().map((c) => ({ value: c, label: c })),
    [],
  );
  const yearError = attempted && !year ? 'Year is required' : undefined;
  const countyError = attempted && !counties.length ? 'At least one county is required' : undefined;

  const submit = async () => {
    setAttempted(true);
    setServerError(null);
    if (!year || !counties.length) return;
    try {
      await changeYear({ pvYear: year, serviceCounties: counties.map(String) }).unwrap();
      toast.success(`Letters for ${year} created`);
      onOpenChange(false);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setServerError(
        status === 409
          ? 'This year has already been created'
          : errorMessage(e, 'Error creating new year'),
      );
    }
  };

  return (
    <DialogMod
      open={open}
      onOpenChange={onOpenChange}
      title='Create new year'
      subtitle=''
      size='sm'
      leftBtnTitle='Cancel'
      rightBtnTitle='Save'
      isProcessing={isLoading}
      onClick={() => void submit()}>
      <div className='rep:flex rep:flex-col rep:gap-4 rep:px-4 rep:py-4'>
        <DropSelect
          fullWidth
          label='Select Year'
          placeholder='Select Year'
          options={yearOptions}
          value={year}
          onChange={(v) => setYear(v ? String(v) : undefined)}
          error={!!yearError}
          helperText={yearError}
        />
        <span className='rep:mt-2 rep:text-sm rep:font-bold rep:text-[#1C43B1] rep:uppercase'>
          Non-evaluated medical service drill
        </span>
        <DialogMultiSelect
          fullWidth
          withBadge
          label='Select counties'
          placeholder='Select counties'
          options={countyOptions}
          value={counties}
          onChange={setCounties}
          error={!!countyError}
          helperText={countyError}
        />
        {serverError ? <p className='rep:text-sm rep:text-destructive'>{serverError}</p> : null}
      </div>
    </DialogMod>
  );
};

export default NewYearDialog;
